// src/pages/DeviceTypeManagement/tabs/MonitoringTab.jsx
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Divider,
  FormControl,
  FormControlLabel,
  Switch,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import {
  MonitorHeart as MonitorIcon,
  Wifi as MqttIcon,
  NetworkPing as PingIcon,
  NotificationsActive as AlertIcon
} from '@mui/icons-material';

const MONITORING_TYPES = [
  { value: 'none', label: 'No Monitoring', description: 'Monitoring disabled for this device type' },
  { value: 'mqtt', label: 'MQTT Heartbeat', description: 'Monitor via MQTT heartbeat messages' },
  { value: 'ping', label: 'Ping Check', description: 'Monitor via network ping' },
  { value: 'both', label: 'Both (MQTT + Ping)', description: 'Monitor using both MQTT heartbeat and ping' }
];

const INTERVAL_PRESETS = [
  { value: 60, label: '1 minute' },
  { value: 120, label: '2 minutes' },
  { value: 180, label: '3 minutes' },
  { value: 300, label: '5 minutes (recommended)' },
  { value: 600, label: '10 minutes' },
  { value: 900, label: '15 minutes' },
  { value: 1800, label: '30 minutes' }
];

const MonitoringTab = ({ deviceType, onChange }) => {
  const monitoringEnabled = deviceType.monitoringEnabled || false;
  const monitoringType = deviceType.monitoringType || 'none';
  const monitoringInterval = deviceType.monitoringInterval || 300;
  const pingTarget = deviceType.pingTarget || '';

  const handleMonitoringEnabledChange = (event) => {
    const enabled = event.target.checked;
    onChange({
      monitoringEnabled: enabled,
      // If disabling, reset type to 'none'
      ...(enabled ? {} : { monitoringType: 'none' })
    });
  };

  const handleMonitoringTypeChange = (event) => {
    onChange({ monitoringType: event.target.value });
  };

  const handleIntervalChange = (event) => {
    onChange({ monitoringInterval: event.target.value });
  };

  const handlePingTargetChange = (event) => {
    onChange({ pingTarget: event.target.value });
  };

  const showPingSettings = monitoringType === 'ping' || monitoringType === 'both';
  const showMqttSettings = monitoringType === 'mqtt' || monitoringType === 'both';

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MonitorIcon color="primary" />
        Device Monitoring Configuration
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* Enable Monitoring */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Enable Monitoring
              </Typography>
              <Typography variant="body2" color="text.secondary">
                When enabled, devices of this type will be monitored for availability.
                Alerts will be sent to christophe@caro.care when devices go offline.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={monitoringEnabled}
                  onChange={handleMonitoringEnabledChange}
                  color="primary"
                />
              }
              label={monitoringEnabled ? 'Enabled' : 'Disabled'}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Monitoring Type */}
      {monitoringEnabled && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monitoring Type
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select how devices of this type should be monitored.
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Monitoring Type</InputLabel>
                <Select
                  value={monitoringType}
                  onChange={handleMonitoringTypeChange}
                  label="Monitoring Type"
                >
                  {MONITORING_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box>
                        <Typography>{type.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {type.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={1}>
                {(monitoringType === 'mqtt' || monitoringType === 'both') && (
                  <Chip
                    icon={<MqttIcon />}
                    label="MQTT Heartbeat"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {(monitoringType === 'ping' || monitoringType === 'both') && (
                  <Chip
                    icon={<PingIcon />}
                    label="Ping Check"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Monitoring Interval */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Check Interval
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                How often should we check if the device is online?
                Recommended: every 5 minutes.
              </Typography>

              <FormControl fullWidth>
                <InputLabel>Check Interval</InputLabel>
                <Select
                  value={monitoringInterval}
                  onChange={handleIntervalChange}
                  label="Check Interval"
                >
                  {INTERVAL_PRESETS.map((preset) => (
                    <MenuItem key={preset.value} value={preset.value}>
                      {preset.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Alert severity="info" sx={{ mt: 2 }}>
                If a device does not respond within 2x the check interval ({(monitoringInterval * 2 / 60).toFixed(0)} minutes),
                it will be marked as offline and a notification will be sent.
              </Alert>
            </CardContent>
          </Card>

          {/* Ping Settings */}
          {showPingSettings && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PingIcon color="secondary" />
                  Ping Configuration
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configure the ping target for network-based monitoring.
                </Typography>

                <TextField
                  fullWidth
                  label="Ping Target (optional)"
                  value={pingTarget}
                  onChange={handlePingTargetChange}
                  placeholder="e.g., 192.168.1.100 or device.local"
                  helperText="IP address or hostname to ping. If left empty, the device's configured IP will be used."
                  sx={{ mb: 2 }}
                />

                <Alert severity="warning">
                  Make sure the device allows ICMP ping requests through its firewall.
                </Alert>
              </CardContent>
            </Card>
          )}

          {/* MQTT Settings Info */}
          {showMqttSettings && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MqttIcon color="primary" />
                  MQTT Heartbeat Configuration
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  MQTT heartbeat monitoring is automatically configured for devices with the CARO Agent installed.
                </Typography>

                <Alert severity="info">
                  Devices send heartbeat messages to the MQTT broker at regular intervals.
                  The system monitors these heartbeats and triggers alerts when they stop.
                </Alert>
              </CardContent>
            </Card>
          )}

          {/* Notification Info */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AlertIcon color="warning" />
                Notifications
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                When a device of this type goes offline, notifications will be sent to:
              </Typography>

              <Chip
                label="christophe@caro.care"
                color="primary"
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" color="text.secondary">
                Additional notifications may be sent to users with access to the device's location.
              </Typography>
            </CardContent>
          </Card>
        </>
      )}

      {/* Disabled State */}
      {!monitoringEnabled && (
        <Alert severity="info">
          Monitoring is disabled for this device type. Enable monitoring above to configure
          availability checks and receive notifications when devices go offline.
        </Alert>
      )}
    </Box>
  );
};

export default MonitoringTab;
